/**
 * Gift Manager — metaobject-driven gift-with-purchase engine.
 * Reads config from #gift-campaigns-config JSON, evaluates conditions
 * against the live cart, and auto-adds / removes / picks gifts.
 */
(function () {
  'use strict';

  var CHECKMARK_SVG =
    '<svg class="gift-picker__checkmark" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<circle cx="8" cy="8" r="8" fill="#2d6a2d"/>' +
      '<path d="M4.5 8.5L7 11L11.5 5.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg> ';

  var TRUCK_SVG =
    '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<path d="M3 6.5h10.5v9H3v-9z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>' +
      '<path d="M13.5 9.5H17l3 3v3h-6.5v-6z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>' +
      '<circle cx="7" cy="17" r="1.6" stroke="currentColor" stroke-width="1.5"/>' +
      '<circle cx="17" cy="17" r="1.6" stroke="currentColor" stroke-width="1.5"/>' +
    '</svg>';

  var GIFT_SVG =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg">' +
      '<rect x="3" y="8" width="18" height="4" rx="1"/>' +
      '<path d="M12 8v13"/>' +
      '<path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"/>' +
      '<path d="M7.5 8a2.5 2.5 0 0 1 0-5A4.8 8 0 0 1 12 8a4.8 8 0 0 1 4.5-5 2.5 2.5 0 0 1 0 5"/>' +
    '</svg>';

  class GiftManager {
    constructor() {
      this.campaigns = [];
      this.campaignGroups = {};
      this._queue = Promise.resolve();
      this._evaluating = false;
      this._giftOpsPending = false;
      this._debounceTimer = null;
      this._lastCartToken = null;
      this._origUpdateCheckout = null;
      this._suppressCartUpdates = false;
      // Eligibility is locked to the customer's own (non-gift) cart state so the
      // gift's presence can't feed back into its own decision (prevents the
      // add/remove oscillation when discounts reallocate). See evaluate().
      this._decidedNonGiftFp = null;
      this._decisions = null;
      this._lastMetrics = null;
      // Campaigns whose gift add.js failed transiently (e.g. 429) for the
      // current non-gift cart state — retried on the next real cart change.
      this._failedAdds = {};
      // Campaigns whose gift add.js failed permanently (e.g. 422 out-of-stock /
      // invalid variant). Not retried for the rest of the session, so we don't
      // waste a doomed request on every cart change. Reset on page reload.
      this._deadGiftCampaigns = {};
      this.init();
    }

    /* ------------------------------------------------------------------ */
    /*  Initialisation                                                     */
    /* ------------------------------------------------------------------ */

    init() {
      const configEl = document.getElementById('gift-campaigns-config');
      this.freeShipping = this._readFreeShippingConfig();

      if (configEl) {
        try {
          this.campaigns = JSON.parse(configEl.textContent);
        } catch (e) {
          this.campaigns = [];
        }
      }

      var hasCampaigns = this.campaigns.length > 0;
      var hasFreeShipping = !!(this.freeShipping && this.freeShipping.show);

      // Boot when there are gift campaigns OR a free-shipping tier to show.
      // Free-shipping-only mode renders the bar but does no gift add/remove.
      if (!hasCampaigns && !hasFreeShipping) return;

      // Group by campaignName for deduplication (empty {} in FS-only mode).
      this.campaignGroups = {};
      this.campaigns.forEach(function (c) {
        var name = c.campaignName;
        if (!this.campaignGroups[name]) this.campaignGroups[name] = [];
        this.campaignGroups[name].push(c);
      }.bind(this));

      // Restore the last locked decision from a previous page so an unchanged
      // cart skips re-deciding on load. Purely an optimisation — evaluating in
      // place already keeps an already-eligible gift across reloads.
      this._restoreDecision();

      // Prevent old React gift system errors
      window.updateCartGiftsStatus = this.onCartUpdate.bind(this);
      window.updateCartGiftsBannerStatus = function () {};

      // Wrap updateCheckoutButtonState to block premature enabling during gift ops.
      var origFn = window.updateCheckoutButtonState;
      if (typeof origFn === 'function') {
        this._origUpdateCheckout = origFn;
        var self = this;
        window.updateCheckoutButtonState = function (cartItems, cartFormType) {
          if (self._giftOpsPending) return;
          return origFn.call(this, cartItems, cartFormType);
        };
      }

      // Subscribe to theme cart events
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
      }

      // Hook into updateCartFormVisibility — cart.js calls this on every
      // successful cart change, the most reliable signal.
      var origCartFormVis = window.updateCartFormVisibility;
      if (typeof origCartFormVis === 'function') {
        var self = this;
        window.updateCartFormVisibility = function () {
          origCartFormVis.apply(this, arguments);
          self.onCartUpdate();
        };
      }

      if (hasCampaigns) {
        this.interceptCheckout();

        // Hold checkout button disabled until initial evaluation completes.
        this._giftOpsPending = true;
        document.querySelectorAll('.cart__checkout-button').forEach(function (btn) {
          btn.setAttribute('disabled', 'disabled');
        });
      }

      this.scheduleEvaluate(null, 0);
    }

    /* ------------------------------------------------------------------ */
    /*  Event hooks                                                        */
    /* ------------------------------------------------------------------ */

    onCartUpdate(payload) {
      if (this._suppressCartUpdates) return;
      // Reuse the cart payload Dawn already fetched so we don't fire another
      // /cart.js for every line-item change. The hook is called in three shapes:
      //   - window.updateCartGiftsStatus(parsedState)  -> payload IS the cart
      //   - subscribe('cart-update', {source, cartData}) -> cart is payload.cartData
      //   - bare onCartUpdate()                          -> no payload, will fetch
      var cart = null;
      if (payload) {
        if (Array.isArray(payload.items)) {
          cart = payload;
        } else if (payload.cartData && Array.isArray(payload.cartData.items)) {
          cart = payload.cartData;
        }
      }
      if (cart) this._pendingCart = cart;
      this.scheduleEvaluate(null, 100);
    }

    scheduleEvaluate(data, delay) {
      clearTimeout(this._debounceTimer);
      var ms = typeof delay === 'number' ? delay : 100;
      this._debounceTimer = setTimeout(function () {
        this.evaluate();
      }.bind(this), ms);
    }

    /* ------------------------------------------------------------------ */
    /*  Decision persistence                                               */
    /*                                                                     */
    /*  Persist the locked decision (keyed to the non-gift cart            */
    /*  fingerprint) so an unchanged cart skips re-deciding on the next     */
    /*  page load. Purely an optimisation: evaluate() measures in place and */
    /*  the enforcement loop already keeps an eligible gift across reloads, */
    /*  so nothing breaks if storage is unavailable and this is skipped.    */
    /* ------------------------------------------------------------------ */

    _persistDecision() {
      try {
        if (!this._decidedNonGiftFp || !this._decisions) return;
        // Store the chosen entry's index within its campaign group (entries are
        // plain JSON config objects), so we can rehydrate against the live config.
        var decided = {};
        var names = Object.keys(this._decisions);
        for (var i = 0; i < names.length; i++) {
          var name = names[i];
          var entry = this._decisions[name];
          var group = this.campaignGroups[name] || [];
          decided[name] = entry ? group.indexOf(entry) : -1;
        }
        window.localStorage.setItem(
          'gm_gift_decision_v1',
          JSON.stringify({ fp: this._decidedNonGiftFp, decided: decided })
        );
      } catch (e) {
        /* storage unavailable (private mode / quota) — non-fatal */
      }
    }

    _restoreDecision() {
      try {
        var raw = window.localStorage.getItem('gm_gift_decision_v1');
        if (!raw) return;
        var payload = JSON.parse(raw);
        if (!payload || typeof payload.fp !== 'string' || !payload.decided) return;
        var decisions = {};
        var names = Object.keys(payload.decided);
        for (var i = 0; i < names.length; i++) {
          var name = names[i];
          var group = this.campaignGroups[name];
          if (!group) continue; // campaign no longer configured — ignore
          var idx = payload.decided[name];
          decisions[name] = (typeof idx === 'number' && idx >= 0 && group[idx]) ? group[idx] : null;
        }
        this._decidedNonGiftFp = payload.fp;
        this._decisions = decisions;
      } catch (e) {
        /* corrupt or unavailable — fall back to a fresh decision */
      }
    }

    /* ------------------------------------------------------------------ */
    /*  Core evaluation                                                    */
    /* ------------------------------------------------------------------ */

    async evaluate() {
      if (this._evaluating) return;
      this._evaluating = true;

      try {
        // Prefer the cart payload Dawn already fetched (set in onCartUpdate);
        // only hit /cart.js when we don't have a fresh one. Consumed once so a
        // re-evaluation after a gift add/remove always re-fetches a clean cart.
        var cart;
        if (this._pendingCart && Array.isArray(this._pendingCart.items)) {
          cart = this._pendingCart;
          this._pendingCart = null;
        } else {
          cart = await this.fetchCart();
        }
        var items = cart.items || [];
        var names = Object.keys(this.campaignGroups);

        // Anti-tamper: cap gift quantities before anything else.
        var tampered = await this.enforceGiftQuantity(items);
        if (tampered) {
          this._evaluating = false;
          this.scheduleEvaluate();
          return;
        }

        // Fingerprint ONLY the customer's own (non-gift) lines. The eligibility
        // decision is keyed to this, so the manager adding/removing a gift —
        // which leaves the non-gift lines unchanged — never re-triggers a
        // decision, which is what prevents any gift add/remove oscillation.
        var nonGiftFp = this.nonGiftFingerprint(items);

        if (nonGiftFp !== this._decidedNonGiftFp) {
          // The customer changed their real cart, so re-decide. We measure IN
          // PLACE — there is no need to remove an existing gift first:
          // buildCartMetrics excludes gift lines, and the spend total is summed
          // from each line's per-unit `price`, which Shopify does NOT reallocate
          // for cart-level discounts. So a gift already in the cart cannot change
          // the result, and an already-eligible cart keeps its gift untouched
          // (no remove/re-add churn, no picker re-prompt). The enforcement loop
          // below removes a gift only when its campaign is no longer eligible.

          var metrics = this.buildCartMetrics(items);
          this._decisions = {};
          this._failedAdds = {}; // new cart state — allow one fresh add attempt
          for (var n = 0; n < names.length; n++) {
            var entries = this.campaignGroups[names[n]];
            var metEntry = null;
            for (var e = 0; e < entries.length; e++) {
              if (this.conditionsMet(entries[e], items, metrics.eligibleTotal, metrics.vendorQty, metrics.handleQty)) {
                metEntry = entries[e];
                break;
              }
            }
            this._decisions[names[n]] = metEntry;
          }
          this._decidedNonGiftFp = nonGiftFp;
          this._lastMetrics = metrics;
          // Persist so this decision survives a reload / cart re-entry and the
          // gift isn't stripped by a fresh re-decision next page load.
          this._persistDecision();
        }

        // Enforce the locked decision. Idempotent: safe to run on every cart
        // event, and a no-op once the cart already matches the decision.
        for (var i = 0; i < names.length; i++) {
          var campaignName = names[i];
          var decision = this._decisions ? this._decisions[campaignName] : null;
          var existingGift = this.findGiftLine(campaignName, items);

          if (decision && !existingGift) {
            if (decision.promotionType === 'customer_choice') {
              // A customer_choice gift is never auto-added — the customer must
              // pick it. So removing the gift (to swap it) or clearing and
              // refilling the cart always lets them choose afresh, rather than
              // forcing the previously selected gift back in.
              this.showPicker(campaignName, decision);
            } else if (this._deadGiftCampaigns[campaignName] ||
                       this._failedAdds[campaignName] === this._decidedNonGiftFp ||
                       !this._campaignHasStock(decision)) {
              // Skip the add when the gift can't be obtained: it already failed
              // (permanently out of stock or for this cart state), OR config
              // already says every variant is out of stock. Checking stock up
              // front avoids the doomed add.js request and the split-second
              // "Adding…" spinner flash that a known-OOS gift would otherwise
              // cause. Do NOT retry — that is what caused the loop/flood.
              // Remember it's dead so later cart events skip it too.
              if (!this._campaignHasStock(decision)) {
                this._deadGiftCampaigns[campaignName] = true;
              }
              // Remove any stale spinner without falsely showing "added to cart".
              this.removeGiftLoading(campaignName);
            } else {
              this._giftOpsPending = true;
              this.showGiftLoading(campaignName, decision);
              await this.enqueue(this.addGift.bind(this, campaignName, decision));
            }
          } else if (!decision && existingGift) {
            this._giftOpsPending = true;
            this.showGiftRemoving(campaignName, existingGift.item);
            await this.enqueue(this.removeGift.bind(this, campaignName, items));
            this.hidePicker(campaignName);
          } else if (!decision) {
            this.hidePicker(campaignName);
          }
        }

        var m = this._lastMetrics || this.buildCartMetrics(items);
        this.updateProgressBanner(names, m.eligibleTotal, m.vendorQty, m.handleQty);

      } catch (err) {
      } finally {
        this._evaluating = false;
        if (this._giftOpsPending) {
          this._giftOpsPending = false;
          this._enableCheckout();
        }
      }
    }

    /* ------------------------------------------------------------------ */
    /*  Condition evaluators                                               */
    /* ------------------------------------------------------------------ */

    conditionsMet(campaign, cartItems, eligibleTotal, vendorQty, handleQty) {
      if (!this.isWithinSchedule(campaign)) return false;
      if (campaign.conditionType === 'qualifying_products') {
        return this.getQualifyingQty(campaign, vendorQty, handleQty) >= (campaign.qualifyingMinQuantity || 1);
      }
      return eligibleTotal >= (campaign.minimumSpend || 0);
    }

    isWithinSchedule(campaign) {
      var now = new Date();
      if (campaign.startDate && now < new Date(campaign.startDate)) return false;
      if (campaign.hasEndDate && campaign.endDate && now > new Date(campaign.endDate)) return false;
      return true;
    }

    // Shared: compute total qualifying qty for a campaign
    getQualifyingQty(campaign, vendorQty, handleQty) {
      var vendors = campaign.qualifyingVendors || [];
      var handles = campaign.qualifyingProductHandles || [];

      if (typeof vendors === 'string') {
        try { vendors = JSON.parse(vendors); } catch (_) { vendors = []; }
      }
      if (typeof handles === 'string') {
        try { handles = JSON.parse(handles); } catch (_) { handles = []; }
      }

      var qty = 0;
      var self = this;
      vendors.forEach(function (v) { qty += vendorQty[self.normalizeVendor(v)] || 0; });
      handles.forEach(function (h) { qty += handleQty[(h || '').toLowerCase()] || 0; });
      return qty;
    }

    /* ------------------------------------------------------------------ */
    /*  Cart helpers                                                       */
    /* ------------------------------------------------------------------ */

    // Build eligibleTotal / vendorQty / handleQty from non-gift items
    buildCartMetrics(items) {
      var eligibleTotal = 0;
      var vendorQty = {};
      var handleQty = {};
      var self = this;

      items.forEach(function (i) {
        if (i.properties && (i.properties._gift_campaign != null || i.properties._gift_item === 'true')) return;
        // Count the DISCOUNTED line total (final_line_price) so the threshold and
        // the "X more to unlock" banner match the cart subtotal the customer
        // actually pays — not the pre-discount price (i.price). Falls back to
        // price × quantity if final_line_price isn't present.
        var lineTotal = (typeof i.final_line_price === 'number') ? i.final_line_price : (i.price * i.quantity);
        eligibleTotal += lineTotal / 100;
        var v = self.normalizeVendor(i.vendor);
        vendorQty[v] = (vendorQty[v] || 0) + i.quantity;
        var h = (i.handle || '').toLowerCase();
        handleQty[h] = (handleQty[h] || 0) + i.quantity;
      });

      return { eligibleTotal: eligibleTotal, vendorQty: vendorQty, handleQty: handleQty };
    }

    async fetchCart() {
      var res = await this.fetchWithRetry('/cart.js', { credentials: 'same-origin' });
      return res.json();
    }

    async fetchWithRetry(url, options, maxRetries) {
      maxRetries = maxRetries || 3;
      for (var attempt = 0; attempt <= maxRetries; attempt++) {
        var res = await fetch(url, options);
        if (res.status === 429 && attempt < maxRetries) {
          await new Promise(function (resolve) {
            setTimeout(resolve, 1000 * (attempt + 1));
          });
          continue;
        }
        return res;
      }
    }

    // POST JSON to cart API with retry + suppression
    async postCart(url, body) {
      this._suppressCartUpdates = true;
      return this.fetchWithRetry(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(body)
      });
    }

    findGiftLine(campaignName, items) {
      for (var i = 0; i < items.length; i++) {
        if (items[i].properties && items[i].properties._gift_campaign === campaignName) {
          return { item: items[i], index: i };
        }
      }
      return null;
    }

    cartFingerprint(items) {
      return items.map(function (i) {
        return i.variant_id + ':' + i.quantity + ':' + (i.properties && i.properties._gift_campaign || '');
      }).join('|');
    }

    // Fingerprint of the customer's own lines only — gift lines excluded — so a
    // gift being added/removed does not change it. The eligibility decision is
    // keyed to this value (see evaluate).
    nonGiftFingerprint(items) {
      return items
        .filter(function (i) {
          return !(i.properties && (i.properties._gift_campaign != null || i.properties._gift_item === 'true'));
        })
        .map(function (i) { return i.variant_id + ':' + i.quantity; })
        .join('|');
    }

    /* ------------------------------------------------------------------ */
    /*  Sequential cart operation queue                                    */
    /* ------------------------------------------------------------------ */

    enqueue(fn) {
      this._queue = this._queue.then(fn).catch(function (err) {
      });
      return this._queue;
    }

    /* ------------------------------------------------------------------ */
    /*  Cart operations                                                    */
    /* ------------------------------------------------------------------ */

    async addGift(campaignName, giftConfig) {
      var options = giftConfig.giftOptions || [];
      if (!options.length || !options[0].variants || !options[0].variants.length) {
        return;
      }
      var firstVariant = options[0].variants[0];
      var variantId = firstVariant.numericId || this.extractNumericId(String(firstVariant.id));
      if (!variantId) {
        return;
      }

      var res = await this.postCart('/cart/add.js', {
        items: [{
          id: variantId,
          quantity: 1,
          properties: { _gift_campaign: campaignName, _auto_added: 'true' }
        }]
      });

      // The gift add can be rejected by Shopify (e.g. 422 when the gift variant
      // is out of stock / unavailable). If we don't stop here, the enforce step
      // sees "qualified but no gift line" and retries forever.
      if (!res || !res.ok) {
        var status = res && res.status;
        if (status && status >= 400 && status < 500 && status !== 429) {
          // Permanent client error (422 out of stock, invalid variant, etc.).
          // Stop attempting this gift for the session — no per-change retries.
          this._deadGiftCampaigns[campaignName] = true;
        } else {
          // Transient (429 rate-limit / 5xx). Allow a retry on the next change.
          this._failedAdds[campaignName] = this._decidedNonGiftFp;
        }
        this._suppressCartUpdates = false;
        // The add failed — drop the spinner without claiming success.
        this.removeGiftLoading(campaignName);
        return;
      }

      this.hideGiftLoading(campaignName);
      this._lastCartToken = null;
      this.refreshCartUI();
    }

    async removeGift(campaignName, cartItems) {
      var gift = this.findGiftLine(campaignName, cartItems);
      if (!gift) return;

      await this.postCart('/cart/change.js', { line: gift.index + 1, quantity: 0 });

      this.hideGiftRemoving(campaignName);
      this._lastCartToken = null;
      this.refreshCartUI();
    }

    async addChosenGift(campaignName, variantId) {
      var numericId = this.extractNumericId(String(variantId));
      if (!numericId) numericId = parseInt(variantId, 10);
      if (!numericId) return;

      await this.postCart('/cart/add.js', {
        items: [{
          id: numericId,
          quantity: 1,
          properties: { _gift_campaign: campaignName, _auto_added: 'false' }
        }]
      });

      this.hidePicker(campaignName);
      this._lastCartToken = null;
      this.refreshCartUI();
    }

    /* ------------------------------------------------------------------ */
    /*  Quantity tampering protection                                      */
    /* ------------------------------------------------------------------ */

    async enforceGiftQuantity(items) {
      var tampered = false;

      for (var i = 0; i < items.length; i++) {
        var item = items[i];
        if (!item.properties || !item.properties._gift_campaign) continue;

        var campaignName = item.properties._gift_campaign;
        var maxQty = 1;

        var group = this.campaignGroups[campaignName];
        if (group) {
          group.forEach(function (c) {
            if (c.maxGiftsPerOrder > maxQty) maxQty = c.maxGiftsPerOrder;
          });
        }

        if (item.quantity > maxQty) {
          tampered = true;
          await this.postCart('/cart/change.js', { line: i + 1, quantity: maxQty });
        }
      }

      if (tampered) this.refreshCartUI();
      return tampered;
    }

    /* ------------------------------------------------------------------ */
    /*  Gift picker UI (customer_choice)                                   */
    /* ------------------------------------------------------------------ */

    showPicker(campaignName, giftConfig) {
      var isCartPage = /^\/cart\b/.test(window.location.pathname);
      var containerId = isCartPage ? 'gift-picker-container' : 'gift-drawer-notifications';
      var container = document.getElementById(containerId);

      if (!container) {
        return;
      }

      var existingPicker = document.getElementById('gift-picker-' + campaignName);
      if (existingPicker) return;

      var options = giftConfig.giftOptions || [];
      if (!options.length) return;

      var picker = document.createElement('div');
      picker.id = 'gift-picker-' + campaignName;
      picker.className = 'gift-picker';
      picker.setAttribute('data-campaign', campaignName);

      var heading = giftConfig.pickerHeading || 'Choose your free gift!';
      var description = giftConfig.pickerDescription || '';

      var html = '<div class="gift-picker__header">';
      html += '<h3 class="gift-picker__heading">' + this.escapeHtml(heading) + '</h3>';
      if (description) {
        html += '<p class="gift-picker__description">' + this.escapeHtml(description) + '</p>';
      }
      html += '</div>';
      html += '<div class="gift-picker__grid">';

      var self = this;

      options.forEach(function (product) {
        html += '<div class="gift-picker__card" data-handle="' + self.escapeHtml(product.handle) + '">';

        html += '<div class="gift-picker__card-inner">';
        html += '<div class="gift-picker__image-wrapper">';
        if (product.imageUrl) {
          html += '<img class="gift-picker__image" src="' + self.escapeHtml(product.imageUrl) + '" alt="' + self.escapeHtml(product.title) + '" loading="lazy" width="300" height="300">';
        }
        html += '</div>';
        html += '<div class="gift-picker__badge"><span class="gift-picker__badge-free">Free Gift</span></div>';
        html += '</div>';

        html += '<div class="gift-picker__info">';
        html += '<p class="gift-picker__title">' + self.escapeHtml(product.title) + '</p>';
        html += '</div>';

        html += '<div class="gift-picker__button-container">';
        html += '<div class="gift-picker__price"><span class="gift-picker__price-free">FREE</span></div>';

        var variants = product.variants || [];
        var defaultVariantId = variants.length ? (variants[0].numericId || variants[0].id) : '';

        if (variants.length > 1) {
          html += '<select class="gift-picker__variant-select" data-campaign="' + self.escapeHtml(campaignName) + '">';
          variants.forEach(function (v) {
            html += '<option value="' + (v.numericId || v.id) + '">' + self.escapeHtml(v.title) + '</option>';
          });
          html += '</select>';
        }

        html += '<button type="button" class="gift-picker__select-btn" data-campaign="' + self.escapeHtml(campaignName) + '" data-variant-id="' + defaultVariantId + '">Select this gift</button>';
        html += '</div>';
        html += '</div>';
      });

      html += '</div>';
      picker.innerHTML = html;
      container.appendChild(picker);

      // Bind variant selectors
      picker.querySelectorAll('.gift-picker__variant-select').forEach(function (select) {
        select.addEventListener('change', function () {
          var btn = this.closest('.gift-picker__card').querySelector('.gift-picker__select-btn');
          if (btn) btn.setAttribute('data-variant-id', this.value);
        });
      });

      // Bind select buttons
      picker.querySelectorAll('.gift-picker__select-btn').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var campaign = this.getAttribute('data-campaign');
          var card = this.closest('.gift-picker__card');
          var select = card.querySelector('.gift-picker__variant-select');
          var variantId = select ? select.value : this.getAttribute('data-variant-id');

          this.disabled = true;
          this.textContent = 'Adding...';
          self.addChosenGift(campaign, variantId);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        });
      });

      if (isCartPage) this.showPickerCallout(campaignName);
    }

    showBanner(campaignName, giftConfig) {
      if (document.getElementById('gift-banner-' + campaignName)) return;

      var banner = document.createElement('div');
      banner.id = 'gift-banner-' + campaignName;
      banner.className = 'gift-picker__banner';
      banner.setAttribute('data-campaign', campaignName);

      var heading = giftConfig.pickerHeading || 'You qualify for a free gift!';
      banner.innerHTML = '<div class="gift-picker__banner-inner">' +
        '<span class="gift-picker__banner-text">' + this.escapeHtml(heading) + '</span>' +
        '<a href="/cart" class="gift-picker__banner-link">Choose your gift</a>' +
        '</div>';

      document.body.appendChild(banner);
    }

    hidePicker(campaignName) {
      var picker = document.getElementById('gift-picker-' + campaignName);
      if (picker) picker.remove();
      var banner = document.getElementById('gift-banner-' + campaignName);
      if (banner) banner.remove();
      this.hidePickerCallout(campaignName);
    }

    clearAllCallouts(preservePickerCallout) {
      var container = document.getElementById('main-cart-footer');
      if (!container) return;
      container.querySelectorAll(
        '.gift-picker__loading, [id^="gift-picker-callout-"], [id^="gift-loading-"], [id^="gift-removing-"]'
      ).forEach(function (el) {
        if (preservePickerCallout && el.id && el.id.indexOf('gift-picker-callout-') === 0) return;
        el.remove();
      });
    }

    hasPickerCallout() {
      var container = document.getElementById('main-cart-footer');
      return container && container.querySelector('[id^="gift-picker-callout-"]') !== null;
    }

    showPickerCallout(campaignName) {
      var calloutId = 'gift-picker-callout-' + campaignName;
      if (document.getElementById(calloutId)) return;

      this.clearAllCallouts();

      var container = document.getElementById('main-cart-footer');
      if (!container) return;

      var el = document.createElement('div');
      el.id = calloutId;
      el.className = 'gift-picker__loading gift-picker__loading--success';
      el.innerHTML = CHECKMARK_SVG +
        '<a href="#gift-picker-' + this.escapeHtml(campaignName) + '" class="gift-picker__callout-link">' +
          'You are eligible to select a free gift! Click here to choose.' +
        '</a>';

      el.querySelector('a').addEventListener('click', function (e) {
        e.preventDefault();
        var picker = document.getElementById('gift-picker-' + campaignName);
        if (picker) picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });

      container.insertBefore(el, container.firstChild);
    }

    hidePickerCallout(campaignName) {
      var el = document.getElementById('gift-picker-callout-' + campaignName);
      if (el) el.remove();
    }

    showGiftLoading(campaignName, giftConfig) {
      if (this.hasPickerCallout()) return;

      var loadingId = 'gift-loading-' + campaignName;
      if (document.getElementById(loadingId)) return;

      this.clearAllCallouts(true);

      var container = document.getElementById('main-cart-footer');
      if (!container) return;

      var giftName = this.decodeHtml(
        (giftConfig.giftOptions && giftConfig.giftOptions[0])
          ? giftConfig.giftOptions[0].title : 'free gift'
      );

      var el = document.createElement('div');
      el.id = loadingId;
      el.className = 'gift-picker__loading';
      el.setAttribute('data-gift-name', giftName);
      el.innerHTML = '<span class="gift-picker__loading-spinner"></span> Adding your ' + this.escapeHtml(giftName) + '...';
      container.insertBefore(el, container.firstChild);
    }

    hideGiftLoading(campaignName) {
      var el = document.getElementById('gift-loading-' + campaignName);
      if (!el) return;

      if (this.hasPickerCallout()) { el.remove(); return; }

      var giftName = this.decodeHtml(el.getAttribute('data-gift-name') || 'free gift');
      el.classList.add('gift-picker__loading--success');
      el.innerHTML = CHECKMARK_SVG + 'Congrats! ' + this.escapeHtml(giftName) + ' added to cart.';
    }

    // Remove the loading element without showing the "Congrats! ... added"
    // success message. Used when the gift add was rejected (e.g. 422 out of
    // stock) so we don't falsely claim a gift was added.
    removeGiftLoading(campaignName) {
      var el = document.getElementById('gift-loading-' + campaignName);
      if (el) el.remove();
    }

    showGiftRemoving(campaignName, cartItem) {
      if (this.hasPickerCallout()) return;

      this.clearAllCallouts(true);

      var container = document.getElementById('main-cart-footer');
      if (!container) return;

      var giftName = this.decodeHtml((cartItem && cartItem.title) || 'free gift');

      var el = document.createElement('div');
      el.id = 'gift-removing-' + campaignName;
      el.className = 'gift-picker__loading gift-picker__loading--removing';
      el.innerHTML = '<span class="gift-picker__loading-spinner"></span> Removing ' + this.escapeHtml(giftName) + ' from cart.';
      container.insertBefore(el, container.firstChild);
    }

    hideGiftRemoving(campaignName) {
      var el = document.getElementById('gift-removing-' + campaignName);
      if (el) el.remove();
    }

    /* ------------------------------------------------------------------ */
    /*  Progress banner                                                    */
    /* ------------------------------------------------------------------ */

    // Read the optional #free-shipping-config JSON block. Returns
    // { threshold, show:true } only when the merchant has configured a
    // positive threshold AND Liquid decided to show it (Singapore only).
    _readFreeShippingConfig() {
      var el = document.getElementById('free-shipping-config');
      if (!el) return null;
      try {
        var cfg = JSON.parse(el.textContent);
        if (!cfg || cfg.show !== true) return null;
        var threshold = parseFloat(cfg.threshold);
        if (!threshold || threshold <= 0) return null;
        return { threshold: threshold, show: true };
      } catch (e) {
        return null;
      }
    }

    // True if at least one gift variant for this campaign is purchasable.
    // A missing `available` field (older cached config) counts as in-stock, so
    // we never hide a banner just because the data isn't there yet.
    _campaignHasStock(campaign) {
      var options = (campaign && campaign.giftOptions) || [];
      if (!options.length) return false; // nothing configured to give
      for (var i = 0; i < options.length; i++) {
        var variants = options[i].variants || [];
        for (var j = 0; j < variants.length; j++) {
          if (variants[j].available !== false) return true;
        }
      }
      return false; // every configured variant is explicitly out of stock
    }

    // ROLLBACK: previous single-message renderer. To revert to the old
    // one-line banner: restore this body, uncomment showProgressBanner
    // below, and delete buildTierModel / renderProgressBar / _renderNotices.
    //
    // updateProgressBanner(campaignNames, eligibleTotal, vendorQty, handleQty) {
    //   var self = this;
    //   var sorted = campaignNames.slice().sort(function (a, b) {
    //     return ((self.campaignGroups[a][0] || {}).minimumSpend || 0) -
    //            ((self.campaignGroups[b][0] || {}).minimumSpend || 0);
    //   });
    //   var bestProgress = null;
    //   var bestName = null;
    //   for (var n = 0; n < sorted.length; n++) {
    //     var name = sorted[n];
    //     var entries = this.campaignGroups[name];
    //     var isMet = false;
    //     for (var e = 0; e < entries.length; e++) {
    //       if (this.conditionsMet(entries[e], null, eligibleTotal, vendorQty, handleQty)) { isMet = true; break; }
    //     }
    //     if (isMet) continue;
    //     if (this._deadGiftCampaigns[name] || !this._campaignHasStock(entries[0])) continue;
    //     if (!bestProgress) {
    //       var progress = this.computeProgress(entries[0], eligibleTotal, vendorQty, handleQty);
    //       if (progress.show) { bestProgress = progress; bestName = name; }
    //     }
    //   }
    //   var container = document.getElementById('gift-progress-container');
    //   if (!container) return;
    //   if (bestProgress) { this.showProgressBanner(bestName, bestProgress.message, bestProgress.imageUrl); }
    //   else { container.innerHTML = ''; }
    // }

    updateProgressBanner(campaignNames, eligibleTotal, vendorQty, handleQty) {
      var model = this.buildTierModel(eligibleTotal, vendorQty, handleQty);
      this.renderProgressBar(model.tiers, model.qtyNotices, eligibleTotal);
    }

    // Build the ordered milestone model from active minimum_spend campaigns
    // and the optional free-shipping tier. qualifying_products campaigns do
    // not map onto a $-axis, so they become text notices instead.
    buildTierModel(eligibleTotal, vendorQty, handleQty) {
      var self = this;
      var tiers = [];
      var qtyNotices = [];
      var names = Object.keys(this.campaignGroups);

      for (var n = 0; n < names.length; n++) {
        var name = names[n];
        var entries = this.campaignGroups[name];

        // A campaign name can group multiple config entries. Handle the two
        // condition types independently so a group that mixes them isn't routed
        // to one path and silently dropped from the other.

        // qualifying_products → text notice (first in-schedule such entry).
        // Reuse the existing "Add N more ..." string from computeProgress.
        for (var q = 0; q < entries.length; q++) {
          var qp = entries[q];
          if (qp.conditionType !== 'qualifying_products' || !self.isWithinSchedule(qp)) continue;
          var prog = self.computeProgress(qp, eligibleTotal, vendorQty, handleQty);
          if (prog.show && prog.message) qtyNotices.push(prog.message);
          break;
        }

        // minimum_spend gift tier. A campaign name can group multiple config
        // entries; evaluate() adds the gift as soon as ANY entry's threshold is
        // met (OR semantics), so the gift effectively unlocks at the LOWEST
        // in-schedule threshold. Use that entry for the node so the bar can't
        // show "locked" while the engine has already added the gift.
        var minEntry = null;
        for (var m = 0; m < entries.length; m++) {
          var en = entries[m];
          if (en.conditionType !== 'minimum_spend') continue;
          if (!self.isWithinSchedule(en)) continue;
          if (!minEntry || (en.minimumSpend || 0) < (minEntry.minimumSpend || 0)) minEntry = en;
        }
        if (!minEntry) continue;

        // Skip ones that can't be earned so we never advertise an
        // out-of-stock / dead campaign on the bar.
        if (self._deadGiftCampaigns[name] || !self._campaignHasStock(minEntry)) continue;

        var giftName = 'a free gift';
        var imageUrl = '';
        if (minEntry.promotionType === 'customer_choice') {
          if (minEntry.pickerHeading) giftName = self.decodeHtml(minEntry.pickerHeading);
        } else if (minEntry.giftOptions && minEntry.giftOptions.length) {
          if (minEntry.giftOptions[0].title) giftName = self.decodeHtml(minEntry.giftOptions[0].title);
          if (minEntry.giftOptions[0].imageUrl) imageUrl = minEntry.giftOptions[0].imageUrl;
        }

        var threshold = minEntry.minimumSpend || 0;
        tiers.push({
          threshold: threshold,
          label: giftName,
          imageUrl: imageUrl,
          kind: 'gift',
          unlocked: eligibleTotal >= threshold
        });
      }

      // Free-shipping tier (Singapore-only, gated in Liquid).
      if (this.freeShipping && this.freeShipping.show) {
        tiers.push({
          threshold: this.freeShipping.threshold,
          label: 'Free Shipping',
          imageUrl: '',
          kind: 'shipping',
          unlocked: eligibleTotal >= this.freeShipping.threshold
        });
      }

      tiers.sort(function (a, b) { return a.threshold - b.threshold; });
      return { tiers: tiers, qtyNotices: qtyNotices };
    }

    renderProgressBar(tiers, qtyNotices, eligibleTotal) {
      var container = document.getElementById('gift-progress-container');
      if (!container) return;

      // No spend tiers from either source: notices only (wrapped in the same
      // .gift-progress-bar card so a qualifying-products-only store keeps the
      // styled banner), else clear the container (preserves the "never show an
      // empty banner" guarantee).
      if (!tiers.length) {
        this._lastFillPct = 0; // reset so the bar animates from 0 when it returns
        container.innerHTML = qtyNotices.length
          ? '<div class="gift-progress-bar">' + this._renderNotices(qtyNotices) + '</div>'
          : '';
        return;
      }

      var maxThreshold = tiers[tiers.length - 1].threshold || 0;
      // fillPct is computed AFTER the markers render (and are possibly moved by
      // _declutterMarkers on mobile) so the fill lands relative to the dots the
      // customer actually sees — see _fillPctForMarkers below.

      // Start the fill at the previously rendered width (0 on first paint) so the
      // CSS width transition animates to the new target after the bar is inserted.
      var startFillPct = (typeof this._lastFillPct === 'number') ? this._lastFillPct : 0;

      // Group tiers that share a threshold into a single "stop" so a gift and
      // free-shipping reward at the same amount render as one node (thumbnails
      // side by side) with a combined headline. tiers are sorted ascending, so
      // same-threshold entries are adjacent.
      var stops = [];
      for (var g = 0; g < tiers.length; g++) {
        var lastStop = stops[stops.length - 1];
        if (lastStop && lastStop.threshold === tiers[g].threshold) {
          lastStop.tiers.push(tiers[g]);
        } else {
          stops.push({ threshold: tiers[g].threshold, tiers: [tiers[g]], unlocked: tiers[g].unlocked });
        }
      }

      // Headline from the lowest unmet stop.
      var firstUnmet = null;
      for (var i = 0; i < stops.length; i++) {
        if (!stops[i].unlocked) { firstUnmet = stops[i]; break; }
      }
      var headline;
      if (firstUnmet) {
        var remaining = firstUnmet.threshold - eligibleTotal;
        if (remaining < 0) remaining = 0;
        var remStr = '$' + remaining.toFixed(2);
        var stopHasShip = firstUnmet.tiers.some(function (t) { return t.kind === 'shipping'; });
        var stopHasGift = firstUnmet.tiers.some(function (t) { return t.kind === 'gift'; });
        if (stopHasShip && !stopHasGift) {
          headline = remStr + ' more for Free Shipping!';
        } else {
          headline = remStr + ' more to unlock ' + this._stopRewardLabel(firstUnmet.tiers) + '!';
        }
      } else {
        var hasGift = tiers.some(function (t) { return t.kind === 'gift'; });
        headline = hasGift
          ? 'All rewards unlocked!'
          : "You've unlocked free shipping!";
      }

      var html = '<div class="gift-progress-bar">';
      html += '<p class="gpb-headline">' + GIFT_SVG + '<span class="gpb-headline-text">' + headline + '</span></p>';
      html += '<div class="gpb-track-wrap">';
      html += '<div class="gpb-track"><div class="gpb-fill" style="width:' + startFillPct.toFixed(2) + '%"></div></div>';

      for (var t = 0; t < stops.length; t++) {
        var stop = stops[t];
        var pos = maxThreshold > 0 ? (stop.threshold / maxThreshold) * 100 : 100;
        html += '<div class="gpb-node' + (stop.unlocked ? ' gpb-node--done' : '') + '" style="left:' + pos.toFixed(2) + '%">';

        // One or more thumbnails, side by side (e.g. truck + gift image when a
        // free-shipping tier and a gift share the same threshold).
        html += '<span class="gpb-thumb">';
        for (var k = 0; k < stop.tiers.length; k++) {
          var st = stop.tiers[k];
          html += '<span class="gpb-thumb-item">';
          if (st.kind === 'shipping') {
            html += TRUCK_SVG;
          } else if (st.imageUrl) {
            html += '<img src="' + this.escapeHtml(st.imageUrl) + '" alt="" width="34" height="34">';
          } else {
            html += GIFT_SVG;
          }
          html += '</span>';
        }
        html += '</span>';

        html += '<span class="gpb-dot">' + (stop.unlocked ? CHECKMARK_SVG : '') + '</span>';
        html += '<span class="gpb-amount">' + this._formatAmount(stop.threshold) + '</span>';
        // Reward name under the price so each marker names what it unlocks.
        // _stopRewardLabel already escapes gift titles, so this is injection-safe.
        html += '<span class="gpb-reward">' + this._stopRewardLabel(stop.tiers) + '</span>';
        html += '</div>';
      }

      html += '</div>'; // .gpb-track-wrap
      if (qtyNotices.length) html += this._renderNotices(qtyNotices);
      html += '</div>'; // .gift-progress-bar

      container.innerHTML = html;

      // On the narrow mobile track, spread any markers whose labels would
      // collide (the dots then sit slightly off their true $ position, which is
      // acceptable — the names stay legible). Runs off the true positions just
      // written above.
      this._declutterMarkers(container);

      // Now that the dots are in their final positions, size the fill relative
      // to THEM (not raw dollars) so it can't visually overshoot a marker the
      // customer hasn't unlocked. No-op vs. the old formula on desktop.
      var fillPct = this._fillPctForMarkers(container, stops, eligibleTotal, maxThreshold);

      // Kick the fill from its start width to the target so the CSS transition
      // animates: on load 0 -> cart %, on a cart change previous % -> new %.
      var fillEl = container.querySelector('.gpb-fill');
      if (fillEl) {
        void fillEl.offsetWidth; // force reflow to lock in the start width first
        fillEl.style.width = fillPct.toFixed(2) + '%';
      }
      this._lastFillPct = fillPct;
    }

    // Fill width as a % of the track, interpolated across the ACTUAL marker
    // positions (which _declutterMarkers may have moved on mobile) rather than
    // raw dollars, so the fill lands exactly on a dot only when that dot's
    // threshold is genuinely met — and sits proportionally between dots
    // otherwise. On desktop, where dots sit at their true linear positions,
    // this is identical to the old eligibleTotal/maxThreshold formula.
    _fillPctForMarkers(container, stops, total, maxThreshold) {
      if (maxThreshold <= 0) return 0;
      var nodes = container.querySelectorAll('.gpb-node');
      if (!nodes.length) return 0;

      // Threshold ↔ (possibly decluttered) display position, in ascending order.
      var pts = [];
      for (var i = 0; i < nodes.length && i < stops.length; i++) {
        pts.push({ t: stops[i].threshold, p: parseFloat(nodes[i].style.left) || 0 });
      }

      // Below the first marker: from the track start (0% at $0) up to it.
      if (total <= pts[0].t) {
        return pts[0].t > 0 ? pts[0].p * (total / pts[0].t) : 0;
      }
      // Between two markers: proportional along that segment.
      for (var j = 0; j < pts.length - 1; j++) {
        if (total <= pts[j + 1].t) {
          var frac = (total - pts[j].t) / (pts[j + 1].t - pts[j].t);
          return pts[j].p + (pts[j + 1].p - pts[j].p) * frac;
        }
      }
      // At or beyond the top tier: pinned to its position (100%).
      return pts[pts.length - 1].p;
    }

    // Mobile-only cosmetic declutter: reward labels are a fixed width, so when
    // two thresholds sit close together (e.g. $80 and $100 on a $130 bar) their
    // names overlap. Enforce a minimum centre-to-centre spacing by walking the
    // markers right-to-left and pulling any that are too close to the next one
    // further left; the highest tier keeps its true position and crowding is
    // pushed back toward the start (clamped at 0). A moved dot no longer sits
    // exactly on its $ amount — acceptable, the labels stay legible. Generic:
    // no tier values are hardcoded, so it adapts to any gift/threshold set.
    _declutterMarkers(container) {
      if (!window.matchMedia('(max-width: 749.98px)').matches) return;
      var wrap = container.querySelector('.gpb-track-wrap');
      if (!wrap) return;
      var nodes = container.querySelectorAll('.gpb-node');
      if (nodes.length < 2) return;

      var trackW = wrap.clientWidth;
      if (!trackW) return; // hidden (e.g. closed drawer): re-runs on next render

      // Min centre gap = the mobile reward-label width (.gpb-reward @<=749.98px)
      // plus a little breathing room, expressed as a % of the track.
      var LABEL_W = 76, GAP = 8;
      var minGapPct = Math.min(100, (LABEL_W + GAP) / trackW * 100);

      var pos = [];
      for (var i = 0; i < nodes.length; i++) pos[i] = parseFloat(nodes[i].style.left) || 0;
      for (var j = nodes.length - 2; j >= 0; j--) {
        if (pos[j + 1] - pos[j] < minGapPct) {
          pos[j] = Math.max(0, pos[j + 1] - minGapPct);
        }
      }
      for (var k = 0; k < nodes.length; k++) nodes[k].style.left = pos[k].toFixed(2) + '%';
    }

    _renderNotices(notices) {
      // Notice strings come from computeProgress, which already escapes any
      // user-supplied gift/qualifier text.
      var html = '<div class="gpb-notices">';
      for (var i = 0; i < notices.length; i++) {
        html += '<p class="gpb-notice">' + notices[i] + '</p>';
      }
      html += '</div>';
      return html;
    }

    // Whole dollars render without cents ($40); fractional thresholds keep 2dp.
    _formatAmount(n) {
      return '$' + (n % 1 === 0 ? String(n) : n.toFixed(2));
    }

    // Combined reward name for the tiers sharing a stop's threshold, e.g.
    // "Free Shipping + Ultra Guard …". Free Shipping (if present) leads; gift
    // names follow, escaped. Joined with " + ".
    _stopRewardLabel(stopTiers) {
      var parts = [];
      if (stopTiers.some(function (t) { return t.kind === 'shipping'; })) {
        parts.push('Free Shipping');
      }
      for (var i = 0; i < stopTiers.length; i++) {
        if (stopTiers[i].kind === 'gift') parts.push(this.escapeHtml(stopTiers[i].label));
      }
      return parts.join(' + ');
    }

    computeProgress(campaign, eligibleTotal, vendorQty, handleQty) {
      if (!this.isWithinSchedule(campaign)) return { show: false, message: '' };

      var giftName = 'a free gift';
      var giftImageUrl = '';
      if (campaign.giftOptions && campaign.giftOptions.length > 0 && campaign.promotionType !== 'customer_choice') {
        if (campaign.giftOptions[0].title) {
          giftName = this.escapeHtml(this.decodeHtml(campaign.giftOptions[0].title));
        }
        if (campaign.giftOptions[0].imageUrl) {
          giftImageUrl = campaign.giftOptions[0].imageUrl;
        }
      }

      if (campaign.conditionType === 'qualifying_products') {
        var minQty = campaign.qualifyingMinQuantity || 1;
        var qty = this.getQualifyingQty(campaign, vendorQty, handleQty);

        if (qty < 1 || qty >= minQty) return { show: false, message: '' };

        var remaining = minQty - qty;
        var vendors = campaign.qualifyingVendors || [];
        var handles = campaign.qualifyingProductHandles || [];
        var qualifier = 'qualifying';
        if (typeof vendors === 'string') {
          try { vendors = JSON.parse(vendors); } catch (_) { vendors = []; }
        }
        if (vendors.length === 1 && (!handles || handles.length === 0)) {
          qualifier = this.decodeHtml(vendors[0]);
        }

        var msg = 'Add ' + remaining + ' more ' + this.escapeHtml(qualifier) +
          ' item' + (remaining > 1 ? 's' : '') + ' to unlock ' + giftName + '!';
        return { show: true, message: msg, imageUrl: giftImageUrl };
      }

      // minimum_spend
      var threshold = campaign.minimumSpend || 0;
      if (eligibleTotal >= threshold) return { show: false, message: '' };

      var remaining = (threshold - eligibleTotal).toFixed(2);
      var msg = '$' + remaining + ' more to unlock ' + giftName + '!';
      return { show: true, message: msg, imageUrl: giftImageUrl };
    }

    // ROLLBACK: single-line banner renderer, unused by the milestone bar.
    // Uncomment together with the old updateProgressBanner body above to revert.
    // showProgressBanner(campaignName, message, imageUrl) {
    //   var bannerId = 'gift-progress-' + campaignName;
    //
    //   var container = document.getElementById('gift-progress-container');
    //   if (!container) return;
    //
    //   var existing = document.getElementById(bannerId);
    //   if (existing) {
    //     var msgEl = existing.querySelector('.gift-picker__progress-text');
    //     if (msgEl && msgEl.innerHTML !== message) msgEl.innerHTML = message;
    //     return;
    //   }
    //
    //   container.innerHTML = '';
    //
    //   var thumbHtml = '';
    //   if (imageUrl) {
    //     thumbHtml = '<img src="' + imageUrl + '" alt="" width="40" height="40" style="width:40px;height:40px;object-fit:cover;border-radius:4px;flex-shrink:0">';
    //   }
    //
    //   var el = document.createElement('div');
    //   el.id = bannerId;
    //   el.className = 'tw-w-full tw-py-m tw-px-l tw-bg-primary-50 tw-border tw-border-[#E3E3E3] tw-border-b-primary-100 tw-rounded-t-sm';
    //   el.innerHTML =
    //     '<div class="tw-flex tw-flex-row tw-justify-center tw-items-center tw-gap-s tw-w-full">' +
    //       thumbHtml +
    //       '<span class="tw-text-headingM tw-font-semiBold tw-text-primary-500 tw-font-heading gift-picker__progress-text" style="font-size:1.5rem">' +
    //         message +
    //       '</span>' +
    //     '</div>';
    //   container.appendChild(el);
    // }

    /* ------------------------------------------------------------------ */
    /*  UI refresh                                                         */
    /* ------------------------------------------------------------------ */

    refreshCartUI() {
      var self = this;
      var isCartPage = /^\/cart\b/.test(window.location.pathname);
      var sectionsToFetch = isCartPage ? 'main-cart-items,cart-drawer' : 'cart-drawer';

      this.fetchWithRetry('/cart?sections=' + sectionsToFetch, { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (sections) {
          if (isCartPage && sections['main-cart-items']) {
            var doc = new DOMParser().parseFromString(sections['main-cart-items'], 'text/html');
            var newContent = doc.querySelector('.js-contents');
            var oldContent = document.querySelector('.js-contents');
            if (newContent && oldContent) oldContent.innerHTML = newContent.innerHTML;

            var newTotals = doc.querySelector('.totals__subtotal-value');
            var oldTotals = document.querySelector('.totals__subtotal-value');
            if (newTotals && oldTotals) oldTotals.textContent = newTotals.textContent;
          }

          var drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.renderContents === 'function' && sections['cart-drawer']) {
            drawer.renderContents({ sections: sections });
          }

          self._enableCheckout();
          setTimeout(function () {
            self._suppressCartUpdates = false;
            // Re-evaluate to catch any cart changes that were suppressed
            self.scheduleEvaluate(null, 100);
          }, 500);
        })
        .catch(function () {
          self._suppressCartUpdates = false;
          self.scheduleEvaluate(null, 100);
          if (isCartPage) window.location.reload();
        });

      setTimeout(function () { self._updateCartCount(); }, 800);
    }

    /* ------------------------------------------------------------------ */
    /*  Checkout button                                                    */
    /* ------------------------------------------------------------------ */

    _enableCheckout() {
      var fn = this._origUpdateCheckout;
      if (!fn) return;
      var formType = window.currentCartFormType;
      if (typeof formType === 'undefined') formType = 'None';

      var cartItems = [];
      document.querySelectorAll('.cart-item').forEach(function (item) {
        cartItems.push({ product_type: item.getAttribute('data-product-type') || '' });
      });
      fn(cartItems, formType);
    }

    _updateCartCount() {
      this.fetchWithRetry('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]').forEach(function (el) {
            el.textContent = cart.item_count;
          });
          document.querySelectorAll('.cart-count-bubble .visually-hidden').forEach(function (el) {
            el.textContent = cart.item_count + (cart.item_count === 1 ? ' item' : ' items');
          });
        })
        .catch(function () {});
    }

    /* ------------------------------------------------------------------ */
    /*  Checkout interception                                              */
    /* ------------------------------------------------------------------ */

    interceptCheckout() {
      var self = this;
      document.addEventListener('submit', function (e) {
        var form = e.target;
        if (form.id !== 'cart' && form.id !== 'CartDrawer-Form') return;
        var submitter = e.submitter;
        if (!submitter || submitter.name !== 'checkout') return;

        e.preventDefault();
        submitter.disabled = true;
        submitter.textContent = 'Validating...';
        self.validateAndCheckout(submitter);
      }, true);
    }

    async validateAndCheckout(submitButton) {
      try {
        var cart = await this.fetchCart();
        var items = cart.items || [];
        var metrics = this.buildCartMetrics(items);

        var removals = [];
        for (var i = 0; i < items.length; i++) {
          var item = items[i];
          if (!item.properties || !item.properties._gift_campaign) continue;

          var campaignName = item.properties._gift_campaign;
          var group = this.campaignGroups[campaignName];
          if (!group) {
            removals.push({ campaignName: campaignName, items: items });
            continue;
          }

          var met = false;
          for (var e = 0; e < group.length; e++) {
            if (this.conditionsMet(group[e], items, metrics.eligibleTotal, metrics.vendorQty, metrics.handleQty)) {
              met = true;
              break;
            }
          }
          if (!met) removals.push({ campaignName: campaignName, items: items });
        }

        if (removals.length > 0) {
          for (var r = 0; r < removals.length; r++) {
            await this.removeGift(removals[r].campaignName, removals[r].items);
          }
          this._lastCartToken = null;
          this.refreshCartUI();
          submitButton.disabled = false;
          submitButton.textContent = submitButton.getAttribute('data-original-text') || 'Check out';
        } else {
          window.location.href = '/checkout';
        }
      } catch (err) {
        window.location.href = '/checkout';
      }
    }

    /* ------------------------------------------------------------------ */
    /*  Utilities                                                          */
    /* ------------------------------------------------------------------ */

    extractNumericId(gid) {
      if (!gid) return null;
      var match = String(gid).match(/(\d+)$/);
      return match ? parseInt(match[1], 10) : null;
    }

    normalizeVendor(str) {
      return (str || '').toLowerCase().replace(/\s*[&+]\s*/g, ' and ').replace(/\s+/g, ' ').trim();
    }

    decodeHtml(str) {
      if (!str) return '';
      var txt = document.createElement('textarea');
      txt.innerHTML = str;
      return txt.value;
    }

    escapeHtml(str) {
      if (!str) return '';
      var div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }
  }

  /* -------------------------------------------------------------------- */
  /*  Boot                                                                 */
  /* -------------------------------------------------------------------- */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      window._giftManager = new GiftManager();
    });
  } else {
    window._giftManager = new GiftManager();
  }
})();
